// Signup/useSignupForm.js
// Custom hook for signup form logic and validation

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../services/firebase';
import { createUserWithEmailAndPassword, sendEmailVerification, updateProfile, signOut } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { formatAuthError, formatValidationError } from '../../utils/errorMessages';
import { FEATURES } from '../../utils/environment';

export const useSignupForm = () => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const navigate = useNavigate();

  // Redirect if already logged in and verified
  useEffect(() => {
    const user = auth.currentUser;
    if (user && user.emailVerified) {
      navigate('/market');
    }
  }, [navigate]);

  const updateField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    // Email validation
    if (!formData.email) {
      errors.email = formatValidationError('email', 'required');
    } else if (!formData.email.endsWith('@ilstu.edu')) {
      errors.email = formatValidationError('email', 'isu-email');
    }

    // Name validation
    if (!formData.firstName.trim()) {
      errors.firstName = formatValidationError('firstName', 'required');
    }
    if (!formData.lastName.trim()) {
      errors.lastName = formatValidationError('lastName', 'required');
    }

    // Password validation
    if (!formData.password) {
      errors.password = formatValidationError('password', 'required');
    } else if (formData.password.length < 8) {
      errors.password = formatValidationError('password', 'password-length');
    } else if (!/\d/.test(formData.password)) {
      errors.password = formatValidationError('password', 'password-number');
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      errors.confirmPassword = formatValidationError('confirmPassword', 'required');
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = formatValidationError('confirmPassword', 'password-match');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSignup = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      // Create the account in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );
      const user = userCredential.user;
      
      // Update the user's display name
      await updateProfile(user, {
        displayName: `${formData.firstName} ${formData.lastName}`
      });

      // Create user profile document in Firestore
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        name: `${formData.firstName} ${formData.lastName}`,
        email: user.email,
        joinedAt: serverTimestamp(),
        profilePic: ''
      });

      // Production safety check - ALWAYS require verification unless on localhost
      const isLocalhost = window.location.hostname === 'localhost';
      const skipVerification = FEATURES.SKIP_EMAIL_VERIFICATION && isLocalhost;

      if (skipVerification) {
        // Development mode ONLY (localhost): Skip email verification
        alert('Account created successfully! You are now logged in. (Development mode - no email verification required)');
        navigate('/market');
      } else {
        // Production mode OR non-localhost: ALWAYS send verification email
        await sendEmailVerification(user, {
          url: window.location.origin + '/verify-email',
          handleCodeInApp: false
        });

        // Sign out the user immediately to force verification
        await signOut(auth);

        // Show success and redirect to login
        alert('Account created successfully! Please check your email (including Junk/Spam folder) for a verification link before logging in.');
        navigate('/login');
      }
      
    } catch (error) {
      setValidationErrors({
        general: formatAuthError(error)
      });
    } finally {
      setLoading(false);
    }
  };

  const navigateToLogin = () => {
    navigate('/login');
  };

  return {
    formData,
    updateField,
    validationErrors,
    loading,
    handleSignup,
    navigateToLogin
  };
};